import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { useLottery, LOTTERIES } from "@/hooks/use-lottery-selection";
import { formatBRL, formatNum } from "@/lib/utils";

// Big-number combinations using BigInt
function combBig(n: number, k: number): bigint {
  if (k < 0 || k > n) return 0n;
  if (k === 0 || k === n) return 1n;
  k = Math.min(k, n - k);
  let num = 1n, den = 1n;
  for (let i = 1; i <= k; i++) {
    num *= BigInt(n - k + i);
    den *= BigInt(i);
  }
  return num / den;
}

function formatBig(n: bigint): string {
  return n.toLocaleString("pt-BR");
}

const Probabilidades = () => {
  const { config } = useLottery();
  const minPick = config.minPick;
  const maxPick = config.maxPick;
  const [picked, setPicked] = useState<number>(minPick);

  // Reset when changing lottery
  useMemo(() => setPicked(minPick), [minPick]);

  // Total possible combinations of `pick` numbers in universe `max`
  const total = useMemo(() => combBig(config.max, config.pick), [config.max, config.pick]);

  // For each match level (k from pick down to floor), compute:
  // P(k acertos | jogo de `picked` números) = C(picked, k) * C(max - picked, pick - k) / C(max, pick)
  // Probability is combinations that give k matches relative to the player's bet of `picked` numbers.
  const rows = useMemo(() => {
    // For player picking `picked` numbers, drawn = config.pick.
    // Hits k means k of the drawn numbers are in player's `picked`.
    // Favorable = C(picked, k) * C(max - picked, drawn - k)
    const drawn = config.pick;
    const picks = picked;
    const max = config.max;
    const denom = combBig(max, drawn);
    const minK = drawn === 6 ? 4 : drawn === 15 ? 11 : drawn === 5 ? 2 : 4;
    const out: { k: number; fav: bigint; oneIn: number; pct: string }[] = [];
    for (let k = drawn; k >= minK; k--) {
      const fav = combBig(picks, k) * combBig(max - picks, drawn - k);
      if (fav === 0n) continue;
      // Approximate odds as number (denom/fav)
      const oneIn = Number(denom * 1000n / fav) / 1000;
      const pct = (Number((fav * 100000000n) / denom) / 1000000).toFixed(6);
      out.push({ k, fav, oneIn, pct });
    }
    return out;
  }, [config.max, config.pick, picked]);

  // Cost estimate per combination — base prices from Caixa
  const costPerGame: Record<string, number> = {
    "mega-sena": 6,
    "lotofacil": 3.5,
    "quina": 3,
    "dupla-sena": 3,
  };
  const costPicks: Record<string, Record<number, number>> = {
    "mega-sena": { 6: 6, 7: 42, 8: 168, 9: 504, 10: 1260, 11: 2772, 12: 5544, 13: 10296, 14: 18018, 15: 30030, 16: 48048, 17: 74256, 18: 111384, 19: 162792, 20: 232560 },
    "lotofacil": { 15: 3.5, 16: 56, 17: 476, 18: 2856, 19: 13566, 20: 54264 },
    "quina": { 5: 3, 6: 18, 7: 63, 8: 168, 9: 378, 10: 756 },
    "dupla-sena": { 6: 3, 7: 21, 8: 84, 9: 252, 10: 630 },
  };
  const cost = costPicks[config.slug]?.[picked] ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-foreground">Probabilidades — {config.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cálculo matemático puro. Sem ilusões. Cada jogo é independente.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-lt-border bg-card md:col-span-1">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Sua aposta</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground">Números marcados</span>
                <span className="font-mono text-2xl text-foreground">{picked}</span>
              </div>
              <Slider
                value={[picked]}
                onValueChange={(v) => setPicked(v[0])}
                min={minPick}
                max={maxPick}
                step={1}
              />
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>{minPick}</span>
                <span>{maxPick}</span>
              </div>
            </div>

            <div className="space-y-2 border-t border-lt-border pt-4 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Universo</span>
                <span className="font-mono text-foreground">1 — {config.max}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sorteados</span>
                <span className="font-mono text-foreground">{config.pick}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Combinações totais</span>
                <span className="font-mono text-foreground">{formatBig(total)}</span>
              </div>
              {cost !== null && (
                <div className="flex justify-between border-t border-lt-border pt-2">
                  <span className="text-muted-foreground">Custo deste jogo</span>
                  <span className="font-mono text-primary">{formatBRL(cost)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-lt-border bg-card md:col-span-2">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Chances de acerto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rows.map((r) => {
                const isMain = r.k === config.pick;
                return (
                  <div
                    key={r.k}
                    className={`flex items-center justify-between rounded-md border px-4 py-3 ${
                      isMain ? "border-primary/40 bg-lt-gold-bg" : "border-lt-border bg-background/40"
                    }`}
                  >
                    <div>
                      <div className="font-serif text-lg text-foreground">{r.k} acertos</div>
                      <div className="text-[11px] text-muted-foreground">{r.pct}%</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">1 em</div>
                      <div className="font-mono text-xl text-foreground">
                        {r.oneIn >= 1_000_000
                          ? formatNum(r.oneIn / 1_000_000, 2) + " mi"
                          : r.oneIn >= 1000
                          ? Math.round(r.oneIn).toLocaleString("pt-BR")
                          : formatNum(r.oneIn, 0)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-lt-border bg-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl">Comparação entre loterias</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {LOTTERIES.map((l) => {
              const t = combBig(l.max, l.pick);
              return (
                <div key={l.slug} className="rounded-md border border-lt-border bg-background/40 p-3">
                  <div className="font-serif text-base text-foreground">{l.name}</div>
                  <div className="mt-1 text-[10px] uppercase text-muted-foreground">prêmio principal</div>
                  <div className="font-mono text-sm text-foreground">1 em {formatBig(t)}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Probabilidades;
